import { FlexyIconButton, useModal } from '@europrocurement/flexy-components';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import React from 'react';

import { type DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { addNoteIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import {
    Layout,
    LAYOUT_DOCUMENTS_FULL,
    LAYOUT_FORMS_FULL,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants';

export type DossierValidationCustomHeaderActionsProps = {
    currentLayout: Layout;
    onLayoutChange: (layout: Layout) => void;
    dossier?: DossierOpenSearch;
    saveBeforeOpenFullForm: () => void;
};
const DossierValidationCustomHeaderActions: React.FunctionComponent<DossierValidationCustomHeaderActionsProps> =
    function ({ currentLayout, onLayoutChange, dossier, saveBeforeOpenFullForm }) {
        const { modalActions } = useModal();
        const { dossierApi } = useFormaliteService();
        const { palette } = useTheme();
        const { enqueueSnackbar } = useSnackbar();
        /**
         * Gère l'ajout d'une nouvelle note
         */
        const handleAddNote = () => {
            modalActions.call(
                <ModalCreateNote
                    defaultMemoClient
                    useMemoClient
                    onConfirm={async (
                        content: string,
                        highlighted: boolean,
                        memoClient: boolean,
                    ) => {
                        if (!dossier?.id) {
                            enqueueSnackbar(`Identifiant du dossier non trouvé`, {
                                variant: 'error',
                            });
                            return;
                        }

                        try {
                            await dossierApi.apiDossiersIdNotesPost({
                                id: dossier?.id.toString(),
                                apiDossiersIdNotesPostRequest: {
                                    content,
                                    highlighted,
                                    memoClient,
                                },
                            });

                            enqueueSnackbar(`Note créée avec succès`);
                        } catch (error: unknown) {
                            console.error('Erreur lors de la création de la note:', error);
                            enqueueSnackbar(`Erreur lors de la création de la note`, {
                                variant: 'error',
                            });
                        }
                    }}
                />,
            );
        };
        return (
            <Box sx={{ display: 'flex', gap: '8px' }}>
                <Button
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0 4px',
                        color: palette.primary.dark,
                        ':hover': {
                            color: palette.primary.main,
                        },
                    }}
                    onClick={() => handleAddNote()}
                >
                    <FlexyIconButton
                        displayTooltip
                        icon={addNoteIcon}
                        onClick={() => {}}
                        tooltipOverwriteProps={{
                            title: 'Ajouter une note',
                        }}
                        iconOverwriteProps={{
                            color: palette.primary.dark,
                        }}
                    />
                    Mémo
                </Button>
                {currentLayout !== LAYOUT_DOCUMENTS_FULL && (
                    <Button
                        onClick={() => onLayoutChange(LAYOUT_DOCUMENTS_FULL)}
                        sx={{
                            color: palette.primary.dark,
                            ':hover': {
                                color: palette.primary.main,
                            },
                            transition: 'color 0.25s ease',
                        }}
                    >
                        <FaOptionIcon
                            sx={{
                                lineHeight: 0,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '14px',
                            }}
                            icon={['fasl', 'arrow-up-right-from-square']}
                        />
                        <Typography variant="l2dcaption">Documents</Typography>
                    </Button>
                )}
                {currentLayout !== LAYOUT_FORMS_FULL && (
                    <Button
                        onClick={saveBeforeOpenFullForm}
                        sx={{
                            color: palette.primary.dark,
                            ':hover': {
                                color: palette.primary.main,
                            },
                        }}
                    >
                        <FaOptionIcon
                            sx={{
                                lineHeight: 0,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '14px',
                            }}
                            icon={['fasl', 'arrow-up-right-from-square']}
                        />
                        <Typography variant="l2dcaption">Formulaires</Typography>
                    </Button>
                )}
            </Box>
        );
    };

export default DossierValidationCustomHeaderActions;
